import * as React from "react";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import { cn } from "../../lib/utils";

type CommandProps = React.ComponentPropsWithoutRef<typeof CommandPrimitive> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive>>;
};

export function Command({ className, ref, ...props }: CommandProps) {
  return (
    <CommandPrimitive
      ref={ref}
      className={cn(
        "flex h-full w-full flex-col overflow-hidden rounded-md bg-surface-container-lowest text-on-surface",
        className,
      )}
      {...props}
    />
  );
}

type InputProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Input
> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Input>>;
};

export function CommandInput({ className, ref, ...props }: InputProps) {
  return (
    <div className="flex items-center gap-2 border-b border-outline-variant/40 px-3">
      <Search className="size-4 shrink-0 text-on-surface-variant" />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          "flex h-10 w-full bg-transparent py-2 text-body-md outline-none placeholder:text-on-surface-variant disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    </div>
  );
}

type ListProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.List
> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.List>>;
};

export function CommandList({ className, ref, ...props }: ListProps) {
  return (
    <CommandPrimitive.List
      ref={ref}
      className={cn("max-h-72 overflow-y-auto overflow-x-hidden", className)}
      {...props}
    />
  );
}

type EmptyProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Empty
> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Empty>>;
};

export function CommandEmpty({ className, ref, ...props }: EmptyProps) {
  return (
    <CommandPrimitive.Empty
      ref={ref}
      className={cn(
        "py-6 text-center text-label-md text-on-surface-variant",
        className,
      )}
      {...props}
    />
  );
}

type GroupProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Group
> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Group>>;
};

export function CommandGroup({ className, ref, ...props }: GroupProps) {
  return (
    <CommandPrimitive.Group
      ref={ref}
      className={cn("overflow-hidden p-1 text-on-surface", className)}
      {...props}
    />
  );
}

type ItemProps = React.ComponentPropsWithoutRef<
  typeof CommandPrimitive.Item
> & {
  ref?: React.Ref<React.ComponentRef<typeof CommandPrimitive.Item>>;
};

export function CommandItem({ className, ref, ...props }: ItemProps) {
  return (
    <CommandPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 text-label-md outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-surface-container data-[disabled=true]:opacity-50",
        className,
      )}
      {...props}
    />
  );
}
